import { Download, ExternalLink, Monitor, Apple, Terminal } from 'lucide-react';
import { SectionLabel } from '@/components/marketing/v2/SectionLabel';
import {
  DESKTOP_PACKAGES,
  GITHUB_RELEASES_URL,
  desktopPackageUrl,
  type DesktopPackage,
} from './shared';

const OS_ICON: Record<DesktopPackage['os'], typeof Monitor> = {
  windows: Monitor,
  mac: Apple,
  linux: Terminal,
};

export default function DownloadMatrixV2() {
  return (
    <section id="download-matrix" className="bg-paper py-20 md:py-24">
      <div className="mx-auto max-w-7xl px-6 md:px-8">
        <SectionLabel>Desktop · tous les formats</SectionLabel>
        <h2 className="mt-3 font-marketing-display text-[clamp(32px,4vw,52px)] font-bold leading-[1] tracking-[-0.035em] text-charcoal-800">
          {DESKTOP_PACKAGES.length} paquets. <span className="underline-lime">Tous vérifiés.</span>
        </h2>
        <p className="mt-4 max-w-[560px] font-marketing-body text-[16px] leading-[1.55] text-charcoal-600">
          Hébergés publiquement sur GitHub Releases, builds Tauri open-source. Prends le format qui
          correspond à ton poste, pas celui qu'on te vend.
        </p>

        <div className="mt-10 overflow-hidden border-[3px] border-charcoal-800 bg-white shadow-neobrutal-10">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] border-collapse">
              <thead>
                <tr className="bg-charcoal-800 text-left text-white">
                  <th className="border-r border-charcoal-700 px-5 py-4 font-marketing-display text-[13px] font-bold uppercase tracking-[0.06em]">
                    Plateforme
                  </th>
                  <th className="border-r border-charcoal-700 px-5 py-4 font-marketing-display text-[13px] font-bold uppercase tracking-[0.06em]">
                    Format
                  </th>
                  <th className="border-r border-charcoal-700 px-5 py-4 font-marketing-display text-[13px] font-bold uppercase tracking-[0.06em]">
                    Architecture
                  </th>
                  <th className="px-5 py-4 font-marketing-display text-[13px] font-bold uppercase tracking-[0.06em]">
                    Fichier
                  </th>
                </tr>
              </thead>
              <tbody>
                {DESKTOP_PACKAGES.map((pkg) => {
                  const Icon = OS_ICON[pkg.os];
                  return (
                    <tr
                      key={pkg.key}
                      className="border-t border-charcoal-100 transition-colors hover:bg-lime-500 hover:text-charcoal-800"
                    >
                      <td className="border-r border-charcoal-100 px-5 py-4">
                        <span className="flex items-center gap-3 font-marketing-body text-[14px] font-semibold text-charcoal-800">
                          <Icon className="h-[18px] w-[18px] text-charcoal-700" strokeWidth={2} />
                          {pkg.osLabel}
                        </span>
                      </td>
                      <td className="border-r border-charcoal-100 px-5 py-4 font-marketing-mono text-[12px] text-charcoal-600">
                        {pkg.format}
                      </td>
                      <td className="border-r border-charcoal-100 px-5 py-4 font-marketing-mono text-[12px] text-charcoal-500">
                        {pkg.arch}
                      </td>
                      <td className="px-5 py-4">
                        <a
                          href={desktopPackageUrl(pkg)}
                          className="inline-flex items-center gap-2 border-2 border-charcoal-800 bg-charcoal-800 px-4 py-2 font-marketing-display text-[12px] font-bold text-lime-500 transition-colors hover:bg-lime-500 hover:text-charcoal-800"
                        >
                          <Download className="h-[14px] w-[14px]" strokeWidth={2.5} />
                          Télécharger
                        </a>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <a
          href={GITHUB_RELEASES_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-5 inline-flex items-center justify-between gap-3 border-2 border-charcoal-800 bg-charcoal-800 px-5 py-3 font-marketing-mono text-[11px] uppercase tracking-[0.08em] text-white"
        >
          <span className="text-charcoal-200">Versions antérieures · changelog complet</span>
          <span className="inline-flex items-center gap-1.5 text-lime-500">
            Voir GitHub Releases
            <ExternalLink className="h-3 w-3" />
          </span>
        </a>
      </div>
    </section>
  );
}
